import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

import { Provider } from '@/shared/components/ui/provider';
import { Router } from 'wouter';

import './index.css';
import { initSentry } from '@/config/sentry.config';

// Inicializar Sentry
initSentry();

// PRAT-19: guarda a origem do acesso (utm, link rastreável, referrer) antes de
// qualquer página limpar os parâmetros da URL (?cupom=, ?ref=).
import {
  captureLanding,
  resendPendingStoreEvents,
} from '@/modules/analytics/store-events/store-events';
captureLanding();
resendPendingStoreEvents();

// PRAT-201: o Chrome do Android avisa cedo que o app pode ser instalado.
import { listenInstallPrompt } from '@/modules/push/install-prompt';
listenInstallPrompt();

// PRAT-209: guarda a variação do apelo do cartão (?cartao=) antes da navegação
// até o checkout tirar o parâmetro da URL.
import { resolveCardCtaVariant } from '@/modules/orders/views/payment-ways/card-cta';
resolveCardCtaVariant();

import { Route, Switch } from 'wouter';
import { StoreThemeSync } from '@/shared/components/layout/store-theme-sync';
import { applyThemeForLocation } from '@/shared/utils/store-theme';

// PRAT-154: cores da loja antes do primeiro render (sem piscar o laranja padrão).
applyThemeForLocation(window.location.pathname);

// PRAT-154: cada tela vira um arquivo próprio, baixado só quando é aberta; a
// página da loja (onde o cliente chega pelo link) segue no pacote inicial.

const StartPage = lazy(() => import('@/modules/auth/pages/start'));
const RegisterPage = lazy(() => import('@/modules/auth/pages/register'));
const AccessPage = lazy(() => import('@/modules/auth/pages/access'));
const OtpPage = lazy(() => import('@/modules/auth/pages/otp'));
const SuccessPage = lazy(() => import('@/modules/auth/pages/success'));
const LoginByLinkPage = lazy(() => import('@/modules/auth/pages/login-by-link'));
const SentryTestPage = lazy(() => import('@/modules/test/pages/sentry-test'));
const ErrorBoundaryTestPage = lazy(() => import('@/modules/test/pages/error-boundary-test'));

const StoresPage = lazy(() => import('@/modules/stores/pages/stores'));
const SearchPage = lazy(() => import('@/modules/stores/pages/search'));
import StorePage from '@/modules/stores/pages/store';
const ListOrdersPage = lazy(() => import('@/modules/orders/pages/list'));
const ReviewOrderPage = lazy(() => import('@/modules/cart/pages/review-order'));
const MakeOrderPage = lazy(() => import('@/modules/orders/pages/make-order'));
const OrderPage = lazy(() => import('@/modules/orders/pages/order'));
const ProfilePage = lazy(() => import('@/modules/account/pages/profile'));
const IndicationPage = lazy(() => import('@/modules/referral/pages/indication'));
const ClubHomePage = lazy(() => import('@/modules/club/pages/home'));
const ClubCatalogPage = lazy(() => import('@/modules/club/pages/catalog'));
const ClubStatementPage = lazy(() => import('@/modules/club/pages/statement'));
const ClubTiersPage = lazy(() => import('@/modules/club/pages/tiers'));
const IndiqueEGanhePage = lazy(() => import('@/modules/store-referral/pages/indique-e-ganhe'));
const CarteiraPage = lazy(() => import('@/modules/wallet/pages/carteira'));
const MinhasIndicacoesPage = lazy(() => import('@/modules/store-referral/pages/minhas-indicacoes'));
const AtendimentoRoutes = lazy(() => import('@/modules/atendimento/routes'));
import { AtendimentoCheckoutGuard } from '@/modules/atendimento/components/checkout-guard';

import { DrawerProvider } from '@/modules/orders/contexts';
import { PendingOrdersProvider } from '@/modules/orders/contexts/pending-orders-context';
import { FirstStoreProvider } from '@/shared/contexts/first-store.context';
import { NavigationProvider } from '@/shared/contexts/navigation-context';
import { AppWrapper } from '@/shared/components/layout/app-wrapper';
import { WebVitalsTracker } from '@/shared/components/sentry/web-vitals-tracker';
import { ErrorFallback } from '@/shared/components/error-boundary';

// Wrapper do Sentry para capturar erros globais
const SentryRouter = Sentry.withSentryRouting(Router);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <Provider>
        <WebVitalsTracker />
        <NavigationProvider>
          <FirstStoreProvider>
            <DrawerProvider>
              <PendingOrdersProvider>
                <SentryRouter>
                  {/* Sacola do atendimento nunca cai no checkout de delivery (PRAT-64) */}
                  <AtendimentoCheckoutGuard />
                  <StoreThemeSync />
                  <Suspense fallback={<div className="min-h-dvh bg-white" />}>
                    <Switch>
                      <Route path="/sentry-test" component={SentryTestPage} />
                      <Route path="/error-boundary-test" component={ErrorBoundaryTestPage} />
                      <Route path="/auth" component={StartPage} />
                      <Route path="/auth/access" component={AccessPage} />
                      <Route path="/auth/register" component={RegisterPage} />
                      <Route path="/auth/otp" component={OtpPage} />
                      <Route path="/auth/success" component={SuccessPage} />
                      <Route path="/login-by-link" component={LoginByLinkPage} />
                      <Route path="/entrar/:key" component={LoginByLinkPage} />
                      <Route path="/entrar" component={LoginByLinkPage} />
                      {/* Atendimento (PRAT-64): tela própria, sem a moldura do app */}
                      <Route path="/:slug/atendimento/*?" component={AtendimentoRoutes} />

                      <AppWrapper>
                        <Switch>
                          <Route path="/search" component={SearchPage} />
                          <Route path="/profile" component={ProfilePage} />
                          <Route path="/indicacao" component={IndicationPage} />
                          <Route path="/orders/:orderId" component={OrderPage} />
                          <Route path="/orders" component={ListOrdersPage} />
                          <Route path="/cart/review-order" component={ReviewOrderPage} />
                          <Route path="/:slug/order/" component={MakeOrderPage} />
                          <Route path="/:slug/clube/catalogo" component={ClubCatalogPage} />
                          <Route path="/:slug/clube/extrato" component={ClubStatementPage} />
                          <Route path="/:slug/clube/niveis" component={ClubTiersPage} />
                          <Route path="/:slug/clube" component={ClubHomePage} />
                          <Route path="/:slug/indique-e-ganhe" component={IndiqueEGanhePage} />
                          <Route path="/:slug/carteira" component={CarteiraPage} />
                          <Route path="/:slug/indicacoes" component={MinhasIndicacoesPage} />
                          <Route path="/:slug" component={StorePage} />
                          <Route path="/" component={StoresPage} />
                        </Switch>
                      </AppWrapper>
                    </Switch>
                  </Suspense>
                </SentryRouter>
              </PendingOrdersProvider>
            </DrawerProvider>
          </FirstStoreProvider>
        </NavigationProvider>
      </Provider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
